import { toast } from '@approvalmax/ui/src/components';
import { mathService } from '@approvalmax/utils';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce, useUnmount } from 'react-use';
import { CreateConversionQuote, CurrencyIsoCode, FixedSide, useCreateConversionQuote } from 'shared/data/wallets/v1';
import { getCountryFlagByCountryCode } from 'shared/helpers';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';
import { WalletAvailableCurrency } from 'shared/hooks/(wallets)';

import { getCountryCodeByCurrency } from '../../Wallets.helpers';
import { messages } from './ConvertCurrencyPopupContent.messages';

export const useCurrenciesInfo = (
    fromCurrency: CurrencyIsoCode | undefined,
    toCurrency: CurrencyIsoCode | undefined,
    currencies: WalletAvailableCurrency[] | undefined
) => {
    const { IconFromFlag, IconToFlag } = useMemo(() => {
        const IconFromFlag = fromCurrency
            ? getCountryFlagByCountryCode(getCountryCodeByCurrency(fromCurrency))
            : undefined;
        const IconToFlag = toCurrency ? getCountryFlagByCountryCode(getCountryCodeByCurrency(toCurrency)) : undefined;

        return {
            IconFromFlag,
            IconToFlag,
        };
    }, [fromCurrency, toCurrency]);

    const { fromBalance, toBalance } = useMemo(() => {
        const fromBalance = currencies?.find((item) => item.id === fromCurrency)?.balance;
        const toBalance = currencies?.find((item) => item.id === toCurrency)?.balance;

        return {
            fromBalance: fromBalance ?? 0,
            toBalance: toBalance ?? 0,
        };
    }, [fromCurrency, toCurrency, currencies]);

    return {
        IconFromFlag,
        IconToFlag,
        fromBalance,
        toBalance,
    };
};

export const useConversionRate = (fromCurrency: CurrencyIsoCode, toCurrency: CurrencyIsoCode) => {
    const { companyId } = useCompanyIdBasedOnUrl();

    const [rate, setRate] = useState<number | null>(null);
    const [rateDate, setRateDate] = useState<string | null>(null);
    const [quoteOptions, setQuoteOptions] = useState<CreateConversionQuote['body'] | null>(null);

    const [, cancel] = useDebounce(
        () => {
            if (!fromCurrency || !toCurrency) {
                setQuoteOptions(null);

                return;
            }

            setQuoteOptions({
                buyCurrency: toCurrency,
                sellCurrency: fromCurrency,
                // as we just need to get a conversion rate, we can set any amount here
                amount: 100,
                fixedSide: FixedSide.sell,
            });
        },
        500,
        [fromCurrency, toCurrency]
    );

    const { mutate: createConversionQuote, isLoading: isLoadingCreateConversionQuote } = useCreateConversionQuote({
        onSuccess: (data) => {
            // From backend we receive rate for 1 buyCurrency = x sellCurrency
            // But for user we show rate as 1 sellCurrency = x buyCurrency
            // So we need to reverse it
            const newRate = mathService.round(mathService.divide(1, data.clientRate), 6);

            setRate(newRate);
            setRateDate(moment().format('DD MMM YYYY HH:mm'));
        },
    });

    useUnmount(() => {
        cancel();
    });

    useEffect(() => {
        if (!quoteOptions) {
            return;
        }

        createConversionQuote({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: quoteOptions,
        });
    }, [quoteOptions, companyId, createConversionQuote]);

    return {
        rate,
        rateDate,
        isLoading: isLoadingCreateConversionQuote,
    };
};

export const useSubmitConversion = (
    fromBalance: number,
    onQuoteCreated: (quote: CreateConversionQuote['response']) => void
) => {
    const { companyId } = useCompanyIdBasedOnUrl();

    const { mutate, isLoading } = useCreateConversionQuote({
        onSuccess: (data) => {
            onQuoteCreated(data);
        },
    });

    const handleSubmitConversion = useCallback(
        (options: {
            fromCurrency: CurrencyIsoCode;
            toCurrency: CurrencyIsoCode;
            amount: number;
            fixedSide: FixedSide;
        }) => {
            const { fromCurrency, toCurrency, amount, fixedSide } = options;

            if (amount > fromBalance) {
                toast.error(messages.insufficientBalance({ currency: fromCurrency }));

                return;
            }

            mutate({
                params: {
                    path: {
                        organizationId: companyId,
                    },
                },
                body: {
                    buyCurrency: toCurrency,
                    sellCurrency: fromCurrency,
                    amount,
                    fixedSide,
                },
            });
        },
        [fromBalance, mutate, companyId]
    );

    return {
        handleSubmitConversion,
        isLoading,
    };
};
